import React, { useState, useMemo } from 'react';
import {
  HbrSelect,
  HbrOption,
  HbrForm,
  HbrInput,
} from '@harbor/elements-react-standalone';
import { MoodleLocation } from '../../../types/moodleEventBooking';
import { moodleFetch, useMoodleLocations } from '../../../hooks/useMoodleEventBooking';
import StbApiModal from '../../security-toolbox-ui/StbApiModal';
import StbButton from '../../security-toolbox-ui/StbButton';
import { renderErrorMessage } from '../../security-toolbox-ui/security-toolbox-ui-utils';
import { asSingleValue } from '../../../support/utils';


type MoodleLocationsSelectProps = {
  preselected?: number; // ID of preselected location
  onChange?: (selected: MoodleLocation | null) => void;
  label?: string;
};

export function MoodleLocationsSelect({
  preselected,
  onChange,
  label = 'Select Location',
}: MoodleLocationsSelectProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState<Partial<MoodleLocation>>({});
  const [userEditedName, setUserEditedName] = useState(false);
  const { data: locations, refetch } = useMoodleLocations();

  const selected = useMemo(
    () => locations?.items.find(l => l.id === preselected) || null,
    [preselected, locations]
  );

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.name === 'name') {
      setUserEditedName(true);
    }
    setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async () => {
    const result = await moodleFetch<MoodleLocation>('locations/', {}, {
      method: 'POST',
      body: JSON.stringify(form),
    });
    setForm({});
    onChange?.(result);
    refetch();
  };

  return (
    <div>
      <div className="flex flex-row items-center gap-2">
        <HbrSelect
          label={label}
          value={selected ? String(selected.id) : ''}
          placeholder="Select location..."
          onHbr-change={e => {
            const val = asSingleValue(e.target.value);
            if (val === '__add__') {
              setModalOpen(true);
            } else {
              const found = locations?.items.find(l => l.id === Number(val));
              onChange?.(found || null);
            }
          }}
          className="flex-1 w-full"
        >
          {locations?.items.map(location => (
            <HbrOption key={location.id} value={String(location.id)}>
              {location.name}
            </HbrOption>
          ))}
          <HbrOption value="__add__" key="__add__">
            + Add Location
          </HbrOption>
        </HbrSelect>
        <StbButton
          role="add"
          size="xsmall"
          variant="text"
          onClick={() => setModalOpen(true)}
          className="mt-3"
        />
      </div>

      <StbApiModal
        open={modalOpen}
        onClose={async () => {
          setModalOpen(false);
          setUserEditedName(false);
          setForm({});
        }}
        title="Add Location"
        onSubmit={handleSubmit}
        role="save"
        submitLabel="Add"
        submitDisabled={!form.name || form.name.trim() === ''}
        submitSuccess="Location added"
        submitError="Error adding location"
      >
        <HbrForm>
          <HbrInput required name="name" value={form.name ?? ''} onHbr-input={(e) => setForm(prev => ({ ...prev, name: asSingleValue(e.target.value) }))}>
            <div slot="label">Name <em className="text-gray-500">(required)</em></div>
            {renderErrorMessage(userEditedName && (!form.name || form.name.trim() === ''), 'Please enter a name')}
          </HbrInput>
          <HbrInput name="address" label="Address" value={form.address ?? ''} onHbr-input={(e) => setForm(prev => ({ ...prev, name: asSingleValue(e.target.value) }))} />
          <HbrInput name="city" label="City" value={form.city ?? ''} onHbr-input={(e) => setForm(prev => ({ ...prev, name: asSingleValue(e.target.value) }))} />
          <HbrInput name="state" label="State" value={form.state ?? ''} onHbr-input={(e) => setForm(prev => ({ ...prev, name: asSingleValue(e.target.value) }))} />
          <HbrInput name="country" label="Country" value={form.country ?? ''} onHbr-input={(e) => setForm(prev => ({ ...prev, name: asSingleValue(e.target.value) }))} />
          <HbrInput name="zip" label="ZIP" value={form.zip ?? ''} onHbr-input={(e) => setForm(prev => ({ ...prev, name: asSingleValue(e.target.value) }))} />
        </HbrForm>
      </StbApiModal>
    </div>
  );
}